import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  PlayArrow,
  AccessTime,
  CalendarToday,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getVideoMetadata, getWeightedRandomVideo } from '../data/videoData';

const PreviewCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(255, 255, 255, 0.05) inset'
    : '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.9) inset',
  backdropFilter: 'blur(12px) saturate(120%)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 0 rgba(255, 255, 255, 0.08) inset'
      : '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 0 rgba(255, 255, 255, 0.95) inset',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
    '& .play-overlay': {
      opacity: 1,
    }
  },
}));

const PlayOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  zIndex: 2,
}));

const parseDuration = (duration) => {
  if (!duration) return 'Unknown';
  
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 'Unknown';
  
  const hours = parseInt(match[1] || 0);
  const minutes = parseInt(match[2] || 0);
  const seconds = parseInt(match[3] || 0);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};

const NextVlogPreview = ({ 
  allVideoIds, 
  currentVideoId, 
  onPlayVideo, 
  userPreferences = {},
  autoRefresh = true,
  selectedEra = null
}) => {
  const theme = useTheme();
  const [nextVideo, setNextVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const getNextVideo = async () => {
    try {
      setIsLoading(true);
      setError(false);

      // Get a weighted random video that's different from current, filtered by era
      const candidateVideo = await getWeightedRandomVideo(selectedEra);
      
      if (!candidateVideo || candidateVideo.id === currentVideoId) {
        // If we get the same video or no video, try to get a different one
        const availableVideos = allVideoIds.filter(id => id !== currentVideoId);
        if (availableVideos.length > 0) {
          const randomIndex = Math.floor(Math.random() * availableVideos.length);
          const fallbackId = availableVideos[randomIndex];
          const fallbackMetadata = await getVideoMetadata(fallbackId);
          setNextVideo(fallbackMetadata);
        } else {
          setNextVideo(candidateVideo);
        }
      } else {
        setNextVideo(candidateVideo);
      }
    } catch (err) {
      console.error('Error getting next video:', err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (allVideoIds.length > 0) {
      getNextVideo();
    }
  }, [allVideoIds, currentVideoId, selectedEra]);

  // Auto-refresh the next video preview periodically
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      getNextVideo();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, allVideoIds, currentVideoId, selectedEra]);

  const handlePlayNext = () => {
    if (nextVideo && onPlayVideo) {
      onPlayVideo(nextVideo.id);
    }
  };

  if (error) {
    return (
      <PreviewCard sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Unable to load next video preview
        </Typography>
      </PreviewCard>
    );
  }

  return (
    <Box>
      <Box 
        sx={{ 
          textAlign: 'center',
          mb: 2,
          position: 'relative'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: '1.1rem',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            position: 'relative',
            display: 'inline-block',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-1.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.2rem',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              height: '2px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              borderRadius: '1px'
            }
          }}
        >
          Up Next
        </Typography>
      </Box>
      
      <PreviewCard onClick={handlePlayNext}>
        <PlayOverlay className="play-overlay">
          <IconButton
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: theme.palette.primary.main,
              fontSize: '3rem',
              '&:hover': {
                bgcolor: 'white',
                transform: 'scale(1.1)',
              }
            }}
          >
            <PlayArrow sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </PlayOverlay>

        {isLoading ? (
          <Box>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px 12px 0 0' }} />
            <CardContent sx={{ p: 1.5 }}>
              <Skeleton variant="text" height={18} />
              <Skeleton variant="text" width="60%" height={14} />
              <Box display="flex" gap={0.5} mt={0.5}>
                <Skeleton variant="rounded" width={50} height={16} />
                <Skeleton variant="rounded" width={35} height={16} />
              </Box>
            </CardContent>
          </Box>
        ) : nextVideo ? (
          <Box>
            <Box sx={{ 
              position: 'relative', 
              overflow: 'hidden',
              borderRadius: '12px 12px 0 0',
              backgroundColor: theme.palette.grey[300],
            }}>
              <CardMedia
                component="img"
                height="100"
                image={`https://img.youtube.com/vi/${nextVideo.id}/maxresdefault.jpg`}
                alt={nextVideo.title}
                sx={{
                  objectFit: 'cover',
                  width: '100%',
                  display: 'block',
                }}
                onError={(e) => {
                  // Fallback to standard quality thumbnail if maxres fails
                  e.target.src = `https://img.youtube.com/vi/${nextVideo.id}/hqdefault.jpg`;
                }}
              />
            </Box>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 2.5 } }}>
              <Typography 
                variant="body2" 
                fontWeight={600}
                color={theme.palette.text.primary}
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.2,
                  mb: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                {nextVideo.title}
              </Typography>
              
              <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                {nextVideo.era && (
                  <Chip
                    label={nextVideo.era}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                )}
                
                {nextVideo.duration && (
                  <Chip
                    icon={<AccessTime sx={{ fontSize: '0.65rem' }} />}
                    label={parseDuration(nextVideo.duration)}
                    size="small"
                    variant="filled"
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      bgcolor: theme.palette.secondary.main,
                      color: '#FFFFFF',
                      '& .MuiChip-label': { px: 0.75 },
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                )}
              </Box>

              {nextVideo.publishedAt && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CalendarToday sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {new Date(nextVideo.publishedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Box>
        ) : (
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 2.5 } }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No preview available
            </Typography>
          </CardContent>
        )}
      </PreviewCard>
    </Box>
  );
};

export default NextVlogPreview;